import { type FC, useEffect, useState } from "react"

import type { LandingPageGalleryBlock } from "@forento/core/models/landing"
import { landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"

import Button from "~/components/Button"
import ImageUploader from "~/components/ImageUploader"
import useFileUpload from "~/hooks/useFileUpload"

import EditableField from "./EditableField"
import EditorShell from "./EditorShell"

type Props = {
	block: LandingPageGalleryBlock
	onChange: (block: LandingPageGalleryBlock) => void
	onClose: () => void
}
const GalleryBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const [title, setTitle] = useState(block.content.title ?? "")
	const [images, setImages] = useState(block.content.images)

	const imageUpload = useFileUpload()

	useEffect(() => {
		onChange({
			type: "gallery",
			content: {
				title: title.trim() || undefined,
				images: images.filter(img => img.url.trim()),
			},
		})
	}, [images, onChange, title])

	const handleRemoveImage = (index: number) => {
		setImages(images.filter((_, i) => i !== index))
	}

	const handleImageChange = (index: number, field: "alt" | "label", value: string) => {
		const newImages = [...images]
		newImages[index] = { ...newImages[index], [field]: value }
		setImages(newImages)
	}

	const handleImageUpload = async (index: number, file: File) => {
		const uploaded = await imageUpload.upload({ file, kind: "landing-image" })
		if (uploaded === null) return
		setImages(prev => prev.map((image, i) => (i === index ? { ...image, url: uploaded.publicUrl } : image)))
	}

	const handleAddImage = async (file: File) => {
		const uploaded = await imageUpload.upload({ file, kind: "landing-image" })
		if (uploaded === null) return
		setImages(prev => [...prev, { url: uploaded.publicUrl, alt: "" }])
	}

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-16 pb-8 sm:pt-20 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<EditableField
						value={title}
						onChange={setTitle}
						placeholder="Section title"
						className="mb-12 text-center"
						maxCharacters={landingPageCopyCharacterLimits.title}
						inputClassName="text-center font-bold text-3xl text-gray-900 tracking-tight sm:text-4xl"
					/>
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{images.map((image, index) => (
							<div key={index} className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
								<div className="relative aspect-video w-full overflow-hidden bg-gray-100">
									{image.url ? (
										<img
											src={image.url}
											alt={image.alt ?? image.label ?? ""}
											className="size-full object-cover"
										/>
									) : (
										<div className="flex size-full items-center justify-center text-gray-400">
											Upload image
										</div>
									)}
								</div>
								<div className="space-y-3 px-4 py-4">
									<EditableField
										value={image.label ?? ""}
										onChange={value => handleImageChange(index, "label", value)}
										placeholder="Caption"
										maxCharacters={landingPageCopyCharacterLimits.title}
										inputClassName="font-medium text-gray-900 text-sm"
									/>
									<EditableField
										value={image.alt ?? ""}
										onChange={value => handleImageChange(index, "alt", value)}
										placeholder="Alt text"
										inputClassName="text-gray-600 text-sm"
									/>
									<ImageUploader
										filePath={image.url || null}
										onUpload={file => handleImageUpload(index, file)}
										onDelete={() => {
											const newImages = [...images]
											newImages[index] = { ...newImages[index], url: "" }
											setImages(newImages)
										}}
									/>
								</div>
								<Button
									variant="secondary-danger"
									onClick={() => handleRemoveImage(index)}
									className="m-4 cursor-pointer"
								>
									Remove image
								</Button>
							</div>
						))}
					</div>
					<div className="mt-8 rounded-2xl border border-dashed border-gray-300 bg-white p-5">
						<ImageUploader
							label="Add image"
							filePath={null}
							onUpload={handleAddImage}
							onDelete={() => {}}
						/>
					</div>
				</div>
			</section>
		</EditorShell>
	)
}

export default GalleryBlockEditor
